/* ============================================================
   THE PRINTED TREATMENT PLAN — one implementation, both designs
   ------------------------------------------------------------
   The page a patient reads is a piece of persuasion: it moves, it fades in, it
   sells a pack. The plan they print is a different object — it goes on a
   bathroom mirror, to a pharmacist, to a doctor. So printing renders a SEPARATE
   document, built from exactly the same resolved data the screen renders from.

   Two result-page designs are installed side by side. A printed plan
   implemented twice would be two clinical documents free to disagree, and they
   would: the four situational notices had already drifted by a word ("updated"
   against "revised") back when they were literals in two places.

   THE INTERFACE IS THREE GLOBALS AND ONE OBJECT
     window.DD_PLAN        the plan AS ISSUED (the snapshot), or null
     window.DD_DATA        the record; only .dd_token is read here
     window.DD_PLAN_COPY   the wording a clinician signed off, or null
     DD_PRINT.build(D)     D is the resolved plan (fields listed below)

   Every function below was moved out of app.js BYTE FOR BYTE, and
   worker/test/printshare.test.mjs fails if any of them drifts from the copy
   app.js still uses on screen. The move was deliberately not a rewrite:
   worker/test/plancopy.test.mjs compares buildPrintDoc against a frozen copy of
   itself to prove the plan-copy refactor changed no output, and rewriting
   during the move would have discarded that guarantee at the moment it was
   most useful.

   D carries: cautions, code, customer, date, flags, ingredients, objectives,
   pack, pigmentRiskOnly, pregnancyOf, pregnancyTeen, pregnant, previous,
   profile, profileMissing, regimen, scores, steroids, title.
   ============================================================ */
(function () {
  "use strict";

  /* Which cleanser size the customer picked, so a conditional gift prints as a
     gift rather than as an offer. Set per build(); {} means "nothing chosen",
     which giftState() correctly reads as not-yet-earned. */
  var GIFT_CHOICE = {};

  /* The SAME cache app.js fills from Shopify, not a second empty one: printed
     prices are the live ones or they are wrong. */
  var PRODUCT_CACHE = window.DD_PRODUCT_CACHE || (window.DD_PRODUCT_CACHE = {});

  /* describeChange() looks up the previous code's profile to say what moved.
     Design 2 does not load the bundled store, and an absent store simply means
     no "since your last assessment" line — never a wrong one. */
  var FALLBACK_STORE = { ingredients: {}, objectives: {}, products: {}, packs: {}, profiles: {} };
  var STORE = (window.DD_STORE && window.DD_STORE.profiles) ? window.DD_STORE : FALLBACK_STORE;

  /* ---------- the wording, as signed off (generated by gen-plan-copy.py) ---------- */
  const PLAN_COPY_FALLBACK = {"doc.kicker":"Dermal Diagnosis™ · Treatment plan","doc.runhead_brand":"Dermal Diagnosis™","doc.meta_prepared":"Prepared for","doc.meta_issued":"Issued","doc.meta_review":"Review","doc.meta_reference":"Reference","doc.footer_org":"DermExcel · Dermal Health Science","doc.disclaimer":"This plan is a guide, not a substitute for professional medical advice.","sec.readings.print":"Your readings","sec.profile.print":"Your skin profile","sec.objectives.print":"Treatment objectives","sec.morning.print":"Morning","sec.evening.print":"Evening","sec.notes.print":"Important notes","sec.actives.print":"Active ingredients in your plan","sec.products.print":"Your products","sec.barriers.screen":"Your skin, mapped as a {connected system}.","sec.readings.screen":"Every parameter, {measured}.","sec.profile.screen":"The {profile} behind your code.","sec.journey.screen":"From where you are, to {where we're taking you}.","sec.objectives.screen":"Your treatment {objectives}.","sec.actives.screen":"Your beneficial {ingredients}.","sec.routine.screen":"Your {regimen}, step by step.","sec.products.screen":"Your pack, {product by product}.","tbl.readings.parameter":"Parameter","tbl.readings.score":"Score","tbl.readings.band":"Band","tbl.readings.meaning":"What it means","tbl.products.pack":"Complete {code} pack","tbl.products.gift":"Included free","fine.readings":"Scores are percentages calculated from your assessment and graded against clinical thresholds. They describe your skin on the day you answered.","fine.products":"Prices are the website prices at the moment this plan was printed, and may have changed since. A dash means we could not confirm the price — please check online. Each product lasts about three months.","fine.products_first":"If you are building up gradually, start with {first}.","since.lead":"Since your last assessment{on_date}:","caution.default_title":"Please read","cta.topbar":"Download plan","cta.keep":"Keep a copy of your plan — stick it on the mirror, or take it to your pharmacist.","cta.button":"Download / print my plan","tbl.products.gift_with":"Free with {item}","gift.locked":"Free with the larger size"};

  const PLAN_ORDER_FALLBACK = ["readings","profile","objectives","routine","notes","actives","products"];

  var NOTICE_FALLBACK = {
    profile_missing: {
      title: "A clinical advisor is reviewing these results.",
      body: "Your answers point to a profile we prepare by hand, so the routine below is "
          + "a general starting point only. Please wait for your personalised plan before "
          + "buying anything."
    },
    pregnancy_dedicated: {
      title: "This is your pregnancy protocol.",
      body: "It was prepared for pregnancy rather than adapted from another routine. "
          + "Confirm every active with your doctor or midwife before you start."
    },
    pregnancy_derived: {
      title: "Adjusted for pregnancy.",
      body: "Retinol and strong exfoliating acids have been removed from the standard "
          + "routine for this skin type. Confirm every active with your doctor or midwife "
          + "before you start."
    },
    protocol_revised: {
      title: "This protocol has been revised since your plan was issued.",
      body: "What you see here is the plan as issued on {issued}. Ask us for the current "
          + "version before you reorder."
    },
    /* Added 2026-10-04 (Trinette). A pregnant teenager keeps her teen protocol;
       the four teen protocols with a pregnancy-unsafe product carry their own
       "If you are pregnant" caution, and this points her to it. Worded to be
       true on both designs: design 1 also filters retinoids from the regimen,
       design 2 shows the protocol as issued. */
    pregnancy_teen: {
      title: "You told us you are pregnant.",
      body: "This is the standard protocol for your skin. Where a step uses a product that "
          + "is avoided in pregnancy, your plan says so under “If you are pregnant” and "
          + "tells you what to use instead. Confirm every active with your doctor or "
          + "midwife before you start."
    },
    /* Added 2026-10-04 (Trinette). {which} names what she ticked on the
       questionnaire's steroid question — steroidList() below. */
    steroid_use: {
      title: "Talk to your practitioner before you start.",
      body: "You told us you use, or have recently used, {which}. Steroids can cause "
          + "breakouts and redness of their own and change how your skin responds to "
          + "treatment. Do not stop a prescribed steroid on your own — your practitioner "
          + "will tell you how this plan fits around it."
    }
  };

  /* ---------- helpers that do not read the plan ---------- */

  function esc(v) {
    return String(v == null ? "" : v)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function todayISO() { return new Date().toISOString().slice(0, 10); }

  function normOpt(v) {
    return String(v == null ? "" : v).toLowerCase().replace(/ml/g, "").replace(/\s+/g, "").trim();
  }

  function fmtDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  }

  function fmtPrice(cents) {
    if (cents == null || isNaN(cents)) return "";
    const cur = (window.DD_CURRENCY && window.DD_CURRENCY.code) || "USD";
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(cents / 100); }
    catch (e) { return ((window.DD_CURRENCY && window.DD_CURRENCY.symbol) || "") + (cents / 100).toFixed(2); }
  }

  function reviewDate(iso, weeks) {
    const d = new Date(iso);
    if (isNaN(d)) return "";
    d.setDate(d.getDate() + (weeks || 12) * 7);
    return fmtDate(d.toISOString());
  }

  function sev(score, mild, mod, severe) {
    if (score < mild) return { label: "Clear", lvl: 0 };
    if (score < mod) return { label: "Mild", lvl: 1 };
    if (score < severe) return { label: "Moderate", lvl: 2 };
    return { label: "Severe", lvl: 3 };
  }

  function catForLabel(label) {
    const l = label.toLowerCase();
    if (l.includes("aging")) return "aging";
    if (l.includes("dry") || l.includes("oily") || l.includes("hydr")) return "hydr";
    if (l.includes("pigment")) return "pig";
    return "infl";
  }

  function hydrationState(s) {
    if (s >= 44.9) {
      const v = s >= 69.9 ? "Severe" : s >= 54.9 ? "Moderate" : "Mild";
      return { state: "Oily", badge: v + " oily", lvl: v === "Severe" ? 3 : v === "Moderate" ? 2 : 1 };
    }
    const v = s < 30 ? "Severe" : s < 40 ? "Moderate" : "Mild";
    return { state: "Dry", badge: v + " dry", lvl: v === "Severe" ? 3 : v === "Moderate" ? 2 : 1 };
  }

  function deriveComponents(title) {
    let t = String(title || "").trim();
    /* "Tween - Congested Pores" does NOT start with "teen", so the old
       /^teen/i test missed it: no minor tile, no prefix stripped, and the
       leftover word rendered as a bogus "T / Tween" component. Both cohorts are
       minors — `teen` still gates "minors never get aging treatment" — but the
       tile has to say which. */
    const cm = /^(tween|teen)\b/i.exec(t);
    const cohort = cm ? cm[1].toLowerCase() : null;
    const teen = !!cohort;
    if (cohort) t = t.replace(/^(tween|teen)\s*[-–:]*\s*/i, "");
    const segs = t.split(/[.,/·]+|\s-\s|\s–\s|\swith\s/i).map((s) => s.trim()).filter(Boolean);
    const comps = []; let modifier = null;
    /* Pulling the modifier word out of a segment leaves the dash that joined it
       behind: "mild-breakouts" becomes "-breakouts". The tile letter is the
       first character of what remains, so that shipped a literal "-" where the
       B belonged, and a tag reading "-Breakouts". The folliculitis branch has
       always cleaned up its dash; the mild branch never did. Both go through
       here now, so neither can drift from the other again. */
    const afterModifier = (x) => String(x).replace(/[-–]+/g, " ").replace(/\s+/g, " ").trim();
    segs.forEach((seg) => {
      let s = seg;
      if (/\bmild\b/i.test(s)) { modifier = { letter: "M", label: "Mild" }; s = afterModifier(s.replace(/\bmild\b/i, "")); }
      if (/folliculitis/i.test(s)) { modifier = { letter: "F", label: "Folliculitis" }; s = afterModifier(s.replace(/folliculitis/i, "")); }
      const low = s.toLowerCase();
      if (!s || low === "skin") return;
      if (low.includes("healthy")) { comps.push({ letter: "✓", label: "Healthy", cat: "hydr" }); return; }
      const letter = /grade/i.test(s) ? "G" + ((s.match(/\d/) || [""])[0]) : s[0].toUpperCase();
      comps.push({ letter, label: s.replace(/\b\w/g, (m) => m.toUpperCase()), cat: catForLabel(s) });
    });
    if (!comps.length) comps.push({ letter: "✓", label: t || "Profile", cat: "hydr" });
    return { comps, modifier, teen, cohort };
  }

  function deriveFlags(comps, teen, modifier) {
    const f = { teen: !!teen, aging: false, hydration: null, pigment: false, inflammation: [] };
    comps.forEach((c) => {
      const l = c.label.toLowerCase();
      if (l.includes("aging")) f.aging = true;
      else if (l.includes("dry")) f.hydration = "dry";
      else if (l.includes("oily")) f.hydration = "oily";
      else if (l.includes("pigment")) f.pigment = true;
      else if (l.includes("breakout") || l.includes("acne")) f.inflammation.push("breakouts");
      else if (l.includes("congested")) f.inflammation.push("congested");
      else if (l.includes("redness")) f.inflammation.push("redness");
      else if (l.includes("allerg")) f.inflammation.push("allergy");
      else if (l.includes("sensitive")) f.inflammation.push("sensitive");
    });
    if (modifier && modifier.label === "Folliculitis") f.inflammation.push("folliculitis");
    return f;
  }

  function describeChange(prevCode, flags) {
    const prevTitle = (STORE.profiles[prevCode] && STORE.profiles[prevCode].title) || prevCode;
    const dc = deriveComponents(prevTitle);
    const pf = deriveFlags(dc.comps, dc.teen, dc.modifier);
    const labels = { breakouts: "breakouts", redness: "redness", allergy: "allergy", sensitive: "sensitivity", folliculitis: "folliculitis", congested: "congestion" };
    const resolved = [], added = [];
    if (pf.pigment && !flags.pigment) resolved.push("pigmentation");
    if (!pf.pigment && flags.pigment) added.push("pigmentation");
    Object.keys(labels).forEach((k) => {
      const inPrev = pf.inflammation.includes(k), inNow = flags.inflammation.includes(k);
      if (inPrev && !inNow) resolved.push(labels[k]);
      if (!inPrev && inNow) added.push(labels[k]);
    });
    if (pf.hydration && flags.hydration && pf.hydration !== flags.hydration) added.push("shift to " + flags.hydration);
    const join = (a) => a.length === 1 ? a[0] : a.slice(0, -1).join(", ") + " and " + a[a.length - 1];
    const parts = [];
    if (resolved.length) parts.push(join(resolved).replace(/^./, (m) => m.toUpperCase()) + " resolved");
    if (added.length) parts.push("now also addressing " + join(added));
    return parts.join(" · ");
  }

  function copy(key, vars) {
    var live = (window.DD_PLAN_COPY || {})[key];
    var t = (live && typeof live.body === "string" && live.body) ? live.body
                                                                 : PLAN_COPY_FALLBACK[key];
    if (t == null) return "";
    t = String(t);
    Object.keys(vars || {}).forEach(function (k) {
      t = t.split("{" + k + "}").join(String(vars[k] == null ? "" : vars[k]));
    });
    /* A placeholder nobody supplied would otherwise print as literal braces on
       a patient's plan. Drop it and close up the space. */
    return t.replace(/\{[a-z_]+\}/g, "").replace(/\s{2,}/g, " ").trim();
  }

  /* The steroid notice names what she ticked, in plain words. The engine sends
     the categories (score-engine.cjs steroidCategories), never the raw answer. */
  var STEROID_WORDS = {
    anabolic: "testosterone or anabolic steroids",
    systemic: "cortisone tablets or injections",
    face_cream: "a cortisone cream on your face"
  };
  function steroidList(v) {
    var w = String(v || "").split(",").map(function (k) { return STEROID_WORDS[k.trim()]; })
      .filter(Boolean);
    return w.length < 2 ? w.join("") : w.slice(0, -1).join(", ") + " and " + w[w.length - 1];
  }

  function notice(slug, vars) {
    var live = (window.DD_NOTICES || {})[slug];
    var n = (live && live.title && live.body) ? live : NOTICE_FALLBACK[slug];
    if (!n) return null;
    var body = String(n.body || "");
    Object.keys(vars || {}).forEach(function (k) {
      body = body.split("{" + k + "}").join(String(vars[k]));
    });
    return { title: String(n.title || ""), body: body };
  }

  function planSections() {
    var live = (window.DD_PLAN_COPY_SECTIONS || null);
    if (!live || !live.length) return PLAN_ORDER_FALLBACK.map(function (k) { return { key: k, visible: 1 }; });
    var known = {};
    PLAN_ORDER_FALLBACK.forEach(function (k) { known[k] = 1; });
    var seen = {}, out = [];
    live.forEach(function (r) {
      /* A stored key the build has never heard of is ignored rather than
         trusted: it would render as nothing, and a blank gap in a plan reads
         as an omission. */
      if (r && known[r.key] && !seen[r.key]) {
        seen[r.key] = 1;
        out.push({ key: r.key, visible: r.visible ? 1 : 0 });
      }
    });
    /* A section the stored order forgot is appended, visible, in build order —
       forgetting is not the same as choosing to hide. */
    PLAN_ORDER_FALLBACK.forEach(function (k) { if (!seen[k]) out.push({ key: k, visible: 1 }); });
    return out;
  }

  function printList(arr, cls) {
    if (!arr || !arr.length) return "";
    return "<ul class=\"" + (cls || "ddp__ul") + "\">" +
      arr.map((x) => "<li>" + x + "</li>").join("") + "</ul>";
  }

  function printNotice(n) {
    if (!n) return "";
    return "<div class=\"ddp__flag\"><b>" + esc(n.title) + "</b> " + esc(n.body) + "</div>";
  }

  function printPrice(handle) {
    const p = handle && PRODUCT_CACHE[handle];
    const v = p && p.variants && p.variants.length ? p.variants[0].price : null;
    return (v != null && !isNaN(v)) ? fmtPrice(v) : "";
  }

  /* A regimen row is read in one order: WHEN, WHAT, HOW. So the function leads
     — it is the eyebrow above the product, not a footnote under it — and the
     product name and the instruction share a top baseline instead of drifting
     apart as the name wraps.

     The size is deliberately not here. "30 ml" told a patient nothing they
     needed while standing at a basin, and set in grey beside a bold name it was
     the loudest piece of noise on the sheet. It stays on the products table,
     which is where someone checks what to reorder. */
  function printSteps(steps) {
    return (steps || []).map((s, i) =>
      "<tr><th class=\"ddp__step\">" + (i + 1) + "</th>" +
      "<td class=\"ddp__rx\">" +
        "<span class=\"ddp__role\">" + esc(s.role) + "</span>" +
        "<b class=\"ddp__rxname\">" + esc(s.name) + "</b>" +
        (s.altName ? "<span class=\"ddp__altline\">" + esc(s.altConjunction || "alternating with") +
          " <b>" + esc(s.altName) + "</b></span>" : "") +
      "</td>" +
      "<td class=\"ddp__note\">" + esc(s.desc) + "</td></tr>"
    ).join("");
  }

  /* ------------------------------------------------------------------
     Everything below reads D, so it is defined per build(). Printing happens
     once in a while; redefining a dozen closures costs nothing, and leaving the
     bodies untouched is what lets the frozen fixture still compare them.
     ------------------------------------------------------------------ */
  function build(D, opts) {
    opts = opts || {};
    GIFT_CHOICE = opts.giftChoice || {};

    function planIssueDate() {
      const snap = window.DD_PLAN || null;
      return (snap && snap.issued_at) || D.date || todayISO();
    }

    function buildScoreCards() {
      const s = D.scores;
      const hy = hydrationState(s.hydration);
      return [
        {
          key: "aging", cat: "aging", name: "Aging", sub: "Chronological + visible signs",
          score: s.aging, badge: s.aging >= 65 ? "Advanced" : s.aging >= 45 ? "Moderate" : "Early",
          foot: "Aging is assessed for everyone over 19. Your reading guides collagen-support and DNA-repair actives."
        },
        {
          key: "hydration", cat: "hydr", name: hy.state === "Dry" ? "Hydration · Dry" : "Hydration · Oily",
          sub: "Sebum & moisture balance", score: s.hydration, badge: hy.badge,
          foot: hy.state === "Oily"
            ? "Higher readings indicate excess sebum — we regulate oil without stripping the barrier."
            : "Lower readings indicate dryness — we rebuild and lock in the moisture barrier."
        },
        D.pigmentRiskOnly
          ? {
              /* You told us you do not have pigmentation — so this reads as a
                 likelihood, never as a finding, and the badge wording never
                 borrows the Clear/Mild/Moderate/Severe scale used for conditions
                 you actually have. */
              key: "pigmentation", cat: "pig", name: "Pigmentation · Risk",
              sub: "Your likelihood, not a current finding",
              score: s.pigmentation,
              badge: s.pigmentation >= 70 ? "Higher risk"
                   : s.pigmentation >= 54 ? "Moderate risk"
                   : s.pigmentation >= 39.9 ? "Some risk" : "Low risk",
              foot: "You told us you do not have pigmentation, so this is a risk score, not a diagnosis. "
                  + "It reflects things like freckling, how your skin tans and family history — "
                  + "daily sun protection is what keeps it from becoming a concern."
            }
          : {
              key: "pigmentation", cat: "pig", name: "Pigmentation", sub: "Melanin & even tone",
              score: s.pigmentation, badge: sev(s.pigmentation, 39.9, 54, 70).label,
              foot: "Pigment is present above 40%. Tyrosinase inhibitors interrupt melanin to even your tone."
            },
        {
          key: "acne", cat: "infl", name: "Acne / Breakouts", sub: "Congestion & lesions",
          score: s.acne, badge: s.acne >= 65 ? "Active" : s.acne >= 45 ? "Moderate" : s.acne >= 30 ? "Mild" : "Clear",
          foot: "Breakout severity blends comedone, papule and pustule signals from your assessment."
        },
        {
          key: "redness", cat: "infl", name: "Redness", sub: "Flushing & rosacea risk",
          score: s.redness, badge: sev(s.redness, 39.9, 54.9, 70).label,
          foot: "Redness is flagged above 40%. Anti-inflammatory actives calm flushing and reactivity."
        },
        {
          key: "allergy", cat: "infl", name: "Allergy", sub: "Barrier reactivity",
          score: s.allergy, badge: sev(s.allergy, 39.9, 54, 70).label,
          foot: "Allergy-prone skin reacts to common irritants — often a sign of an impaired barrier."
        },
        {
          key: "sensitivity", cat: "infl", name: "Sensitivity", sub: "Tolerance threshold",
          score: s.sensitivity, badge: s.sensitivity >= 65 ? "High" : s.sensitivity >= 49 ? "Sensitive" : "Resilient",
          foot: "Above 49% indicates sensitive skin — we introduce actives gradually to build tolerance."
        }
      ];
    }

    function printReadings() {
      return buildScoreCards().map((c) =>
        "<tr><th>" + esc(c.name) + "</th>" +
        /* A bare "73" is not a quantity a patient can place. Every score in
           this table is a percentage of the assessment's own scale, which the
           fine print under the table already says, so the sheet says it on
           each row too. */
        "<td class=\"ddp__num\">" + Math.round(c.score) + "<span class=\"ddp__pct\">%</span></td>" +
        "<td>" + esc(c.badge) + "</td>" +
        "<td class=\"ddp__note\">" + esc(c.foot) + "</td></tr>"
      ).join("");
    }

    function giftState(g) {
      if (!g || !g.variantGroup) return null;
      const chosen = GIFT_CHOICE[g.variantGroup];
      if (chosen == null || chosen === "") return false;
      return normOpt(chosen) === normOpt(g.variantValue);
    }

    function giftCondition(g) {
      const match = (D.pack.items || []).filter((i) =>
        i && i.variantGroup === g.variantGroup && normOpt(i.variantValue) === normOpt(g.variantValue))[0];
      const what = match ? ((match.name || match.handle) + (match.size ? " " + match.size : "")) : "";
      return what ? copy("tbl.products.gift_with", { item: what }) : copy("gift.locked");
    }

    function giftHandles() {
      const set = {};
      (D.pack.gifts || []).forEach((g) => {
        if (g && g.handle && giftState(g) !== false) set[g.handle] = 1;
      });
      return set;
    }

    function packPriceLabel() {
      return printPrice(D.pack.handle);
    }

    function printProducts() {
      const seen = [], out = [], free = giftHandles();
      ["morning", "evening"].forEach((ph) => {
        (D.regimen[ph] || []).forEach((s) => {
          [[s.handle, s.name, s.price, s.size], s.alt ? [s.alt, s.altName, "", ""] : null]
            .filter(Boolean)
            .forEach(([handle, name, price, size]) => {
              if (!name || seen.indexOf(name) >= 0) return;
              seen.push(name);
              const money = free[handle] ? "Included free" : printPrice(handle);
              out.push("<tr><th>" + esc(name) +
                (size ? " <span class=\"ddp__size\">" + esc(size) + "</span>" : "") + "</th>" +
                "<td class=\"ddp__num\">" + (money ? esc(money) : "&mdash;") + "</td></tr>");
            });
        });
      });
      return out.join("");
    }

    function buildPrintDoc() {
      const issued = planIssueDate();
      const snap = window.DD_PLAN || null;
      const weeks = (snap && snap.review_weeks) || 12;
      const ref = (window.DD_DATA && window.DD_DATA.dd_token) || "";
      const who = [D.customer.firstName, D.customer.lastName].filter(Boolean).join(" ").trim();

      const head =
        "<div class=\"ddp__runhead\"><span>" + esc(copy("doc.runhead_brand")) + "</span>" +
        "<span>" + esc(who || "Your plan") + " &middot; " + esc(D.code) + " &middot; " + esc(fmtDate(issued)) + "</span></div>";

      const foot =
        "<div class=\"ddp__runfoot\">" +
        (ref ? "Reference " + esc(ref) + " &middot; " : "") +
        esc(copy("doc.footer_org")) + " &middot; " +
        esc(copy("doc.disclaimer")) + "</div>";

      let body = "";

      /* ---- masthead ---- */
      body +=
        "<header class=\"ddp__mast\">" +
        "<p class=\"ddp__kicker\">" + esc(copy("doc.kicker")) + "</p>" +
        "<h1>" + esc(D.code) + "</h1>" +
        "<p class=\"ddp__word\">" + esc(D.title || D.code) + "</p>" +
        "<dl class=\"ddp__meta\">" +
        (who ? "<div><dt>" + esc(copy("doc.meta_prepared")) + "</dt><dd>" + esc(who) + "</dd></div>" : "") +
        "<div><dt>" + esc(copy("doc.meta_issued")) + "</dt><dd>" + esc(fmtDate(issued)) + "</dd></div>" +
        "<div><dt>" + esc(copy("doc.meta_review")) + "</dt><dd>" + esc(reviewDate(issued, weeks)) + "</dd></div>" +
        (ref ? "<div><dt>" + esc(copy("doc.meta_reference")) + "</dt><dd>" + esc(ref) + "</dd></div>" : "") +
        "</dl></header>";

      /* ---- anything that changes how this plan must be read comes FIRST ---- */
      if (D.profileMissing) {
        body += printNotice(notice("profile_missing"));
      }
      if (D.pregnant) {
        /* Two different things, and telling a patient the wrong one matters. A
           signed-off pregnancy protocol was written for pregnancy; a derived one
           is the standard protocol with contraindicated actives taken out by
           this page as a safety net. Only the first is a prescription. */
        const dedicated = !!D.pregnancyOf ||
          ((snap && snap.safety && snap.safety.source) === "signed-off-protocol");
        /* A pregnant teenager is on her teen protocol, which carries its own
           caution — "adjusted" would be false on design 2, which removes nothing. */
        if (D.pregnancyTeen) body += printNotice(notice("pregnancy_teen"));
        else body += printNotice(notice(dedicated ? "pregnancy_dedicated" : "pregnancy_derived"));
      }
      if (steroidList(D.steroids)) {
        body += printNotice(notice("steroid_use", { which: steroidList(D.steroids) }));
      }
      if (snap && snap.revised) {
        body += printNotice(notice("protocol_revised", { issued: fmtDate(issued) }));
      }
      if (D.previous) {
        const change = describeChange(D.previous.code, D.flags);
        if (change) {
          body += "<div class=\"ddp__since\"><b>" +
            esc(copy("since.lead", { on_date: D.previous.date ? " on " + fmtDate(D.previous.date) : "" })) +
            "</b> " + esc(change) + ".</div>";
        }
      }

      /* Safety copy authored against this protocol. On paper it goes above the
         readings, because a plan that is read once is read from the top. */
      (D.cautions || []).forEach((c) => {
        const items = (c.items || []).filter(Boolean);
        if (!items.length && !c.subtext) return;
        body += "<div class=\"ddp__flag\"><b>" + esc(c.title || copy("caution.default_title")) + "</b> " +
          (c.subtext ? esc(c.subtext) + " " : "") + items.map(esc).join(" ") + "</div>";
      });

      /* Everything from here down is reorderable and, apart from the routine,
         can be switched off — so each section is built into its own slot and the
         document is assembled at the end, in the stored order. Nothing above this
         line moves: the masthead identifies the document, and a safety notice
         below the fold is not a safety notice. */
      const parts = { readings: "", profile: "", objectives: "", routine: "", notes: "", actives: "", products: "" };

      /* ---- readings ---- */
      parts.readings +=
        "<section class=\"ddp__sec\"><h2>" + esc(copy("sec.readings.print")) + "</h2>" +
        "<table class=\"ddp__tbl ddp__tbl--read\"><thead><tr>" +
        "<th>" + esc(copy("tbl.readings.parameter")) + "</th>" +
        "<th class=\"ddp__num\">" + esc(copy("tbl.readings.score")) + "</th>" +
        "<th>" + esc(copy("tbl.readings.band")) + "</th>" +
        "<th>" + esc(copy("tbl.readings.meaning")) + "</th>" +
        "</tr></thead><tbody>" + printReadings() + "</tbody></table>" +
        "<p class=\"ddp__fine\">" + esc(copy("fine.readings")) + "</p></section>";

      /* ---- profile ---- */
      if ((D.profile.description || []).length) {
        parts.profile +=
          "<section class=\"ddp__sec ddp__sec--flow\"><h2>" + esc(copy("sec.profile.print")) + "</h2>" +
          (D.profile.characteristics.length
            ? "<p class=\"ddp__chips\">" + D.profile.characteristics.map(esc).join(" &nbsp;&middot;&nbsp; ") + "</p>"
            : "") +
          D.profile.description.map((x) => "<p>" + x + "</p>").join("") +
          "</section>";
      }

      /* ---- objectives ---- */
      if ((D.objectives || []).length) {
        parts.objectives +=
          /* A table, not a numbered list. The objectives are not steps in an
             order — they are the six things this protocol is FOR — and set as
             an <ol> they read as a sequence and left two thirds of the page
             empty. Same two-column shape as the actives table, so the document
             has one way of presenting a labelled thing. */
          "<section class=\"ddp__sec\"><h2>" + esc(copy("sec.objectives.print")) + "</h2>" +
          "<table class=\"ddp__tbl ddp__tbl--obj\"><tbody>" +
          D.objectives.map((o) =>
            "<tr><th>" + esc(o.t) + "</th><td class=\"ddp__note\">" + esc(o.d) + "</td></tr>").join("") +
          "</tbody></table></section>";
      }

      /* ---- the routine ---- */
      /* Morning and evening are ONE object — the day — and land on one page.
         Wrapped together and marked break-inside:avoid, because a routine split
         across a fold is read as two routines, and the second half is the one
         that gets forgotten. */
      parts.routine +=
        "<div class=\"ddp__day\">" +
        "<section class=\"ddp__sec ddp__sec--rx\"><h2>" + esc(copy("sec.morning.print")) + "</h2>" +
        "<table class=\"ddp__tbl ddp__tbl--rx\"><tbody>" + printSteps(D.regimen.morning) + "</tbody></table></section>" +
        "<section class=\"ddp__sec ddp__sec--rx\"><h2>" + esc(copy("sec.evening.print")) + "</h2>" +
        "<table class=\"ddp__tbl ddp__tbl--rx\"><tbody>" + printSteps(D.regimen.evening) + "</tbody></table></section>" +
        "</div>";

      (D.regimen.intensify || []).forEach((a) => {
        parts.routine += "<section class=\"ddp__sec\"><h2>" + esc(a.title) + "</h2>" + printList(a.items.map(esc)) + "</section>";
      });

      if ((D.regimen.notes || []).length) {
        parts.notes += "<section class=\"ddp__sec\"><h2>" + esc(copy("sec.notes.print")) + "</h2>" +
          printList(D.regimen.notes.map(esc)) + "</section>";
      }

      /* ---- actives ---- */
      if ((D.ingredients || []).length) {
        parts.actives += "<section class=\"ddp__sec\"><h2>" + esc(copy("sec.actives.print")) + "</h2>" +
          "<table class=\"ddp__tbl ddp__tbl--act\"><tbody>" +
          D.ingredients.map((i) =>
            /* An active with no authored role rendered as "— Arbutin", a dash
               introducing nothing. Join only what exists. */
            "<tr><th>" + esc(i.name) + "</th><td class=\"ddp__note\">" +
              [i.role, i.tag].filter(Boolean).map(esc).join(" &mdash; ") + "</td></tr>"
          ).join("") + "</tbody></table></section>";
      }

      /* ---- what to buy ---- */
      parts.products +=
        "<section class=\"ddp__sec\"><h2>" + esc(copy("sec.products.print")) + "</h2>" +
        "<table class=\"ddp__tbl ddp__tbl--buy\"><tbody>" + printProducts() +
        (function () {
          const packPrice = packPriceLabel();
          return packPrice
            ? "<tr class=\"ddp__total\"><th>" + esc(copy("tbl.products.pack", { code: D.code })) + "</th>" +
              "<td class=\"ddp__num\">" + esc(packPrice) + "</td></tr>"
            : "";
        })() +
        (function () {
          const named = {};
          ["morning", "evening"].forEach((ph) => (D.regimen[ph] || []).forEach((s) => {
            if (s.handle) named[s.handle] = 1;
            if (s.alt) named[s.alt] = 1;
          }));
          /* Paper cannot react to a size pill, so a conditional gift states what
             earns it rather than claiming to be in the box. If the choice HAS
             been made on screen before printing, the sheet prints that answer. */
          return (D.pack.gifts || []).filter((g) => !named[g.handle]).map((g) =>
            "<tr><th>" + esc(g.name || g.handle) +
            (g.size ? " <span class=\"ddp__size\">" + esc(g.size) + "</span>" : "") + "</th>" +
            "<td class=\"ddp__num\">" +
            esc(giftState(g) === false ? giftCondition(g) : copy("tbl.products.gift")) +
            "</td></tr>").join("");
        })() +
        "</tbody></table>" +
        "<p class=\"ddp__fine\">" + esc(copy("fine.products")) +
        (D.pack.first ? " " + esc(copy("fine.products_first", { first: D.pack.first })) : "") +
        "</p></section>";

      /* The stored order decides what follows the notices, and a section that is
         switched off is simply never appended. A section with nothing in it —
         no objectives on this protocol, say — contributes nothing either way. */
      planSections().forEach(function (sec) {
        if (sec.visible) body += parts[sec.key] || "";
      });

      const wrap = document.createElement("div");
      wrap.id = "ddPrint";
      wrap.setAttribute("aria-hidden", "true");
      /* thead/tfoot inside a wrapping table is the only technique browsers agree
         on for repeating a running header and footer on every printed page. A
         loose page of a medical document with no name on it is a real problem,
         so it is worth the slightly odd markup. */
      wrap.innerHTML =
        "<table class=\"ddp__sheet\"><thead><tr><td>" + head + "</td></tr></thead>" +
        "<tfoot><tr><td>" + foot + "</td></tr></tfoot>" +
        "<tbody><tr><td>" + body + "</td></tr></tbody></table>";
      return wrap;
    }

    var doc = buildPrintDoc();

    /* A sheet built from the section's Test display setting carries illustrative
       readings for a protocol nobody was assessed against. On screen the page
       says so; once this is a PDF sitting on a desktop, nothing about the file
       does — and it is headed "Treatment plan", has a name slot and a review
       date. So it says what it is on its own face, above the code, where it
       cannot be scrolled past.

       Stamped HERE rather than inside buildPrintDoc() on purpose: the clinical
       document is left untouched, so a real plan is byte for byte the sheet it
       always was, and worker/test/plancopy.test.mjs still compares the function
       it was written to compare. */
    if (window.DD_SAMPLE_PLAN === true) {
      var cell = doc.querySelector(".ddp__sheet > tbody > tr > td");
      if (cell) {
        var stamp = document.createElement("p");
        stamp.className = "ddp__sample";
        stamp.textContent =
          "Sample — not a treatment plan. The readings below are illustrative; " +
          "no one was assessed for this protocol.";
        cell.insertBefore(stamp, cell.firstChild);
      }
    }
    return doc;
  }

  /* Wire the buttons and Ctrl/Cmd-P. `getD` is a function, not a value: live
     product prices and the pack price arrive after boot, so a plan printed at
     10:00 must not carry the prices the page had at 09:59. */
  function mount(getD, opts) {
    var doc = null;
    function ensure() {
      if (doc && doc.parentNode) doc.parentNode.removeChild(doc);
      doc = build(getD(), typeof opts === "function" ? opts() : opts);
      document.body.appendChild(doc);
      return doc;
    }
    function printPlan() { ensure(); window.print(); }

    window.addEventListener("beforeprint", ensure);
    if (window.matchMedia) {
      var mq = window.matchMedia("print");
      var onChange = function (e) { if (e.matches) ensure(); };
      if (mq.addEventListener) mq.addEventListener("change", onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }
    return { print: printPlan, ensure: ensure };
  }

  /* ------------------------------------------------------------------
     THE ADAPTER — /v1/diagnosis + the record  ->  D
     ------------------------------------------------------------------
     Design 1 builds D from the bundled /content store. Design 2 has never
     loaded that store: it reads one protocol from /v1/diagnosis and the
     respondent's record from /r/<token>. Both must produce the same printed
     sheet, so exactly one of them is allowed to be a translation, and this is
     it — kept next to build() rather than inside a theme section, because the
     shape it is translating INTO is defined here.

     Nothing here invents clinical content. Every field is either copied across
     or left empty, and an empty field simply drops its section from the sheet.
     ------------------------------------------------------------------ */
  function num(v) {
    var n = parseFloat(v);
    return isNaN(n) ? null : n;
  }

  /* content-store.cjs reads the lead heading out of the authored benefits text
     the same way; the printed sheet must not depend on which design built it. */
  function leadHeading(v) {
    var s = String(v == null ? "" : v).trim();
    if (!s || /<\w+[\s>]/.test(s)) return "";
    var m = /^([A-Z][^\n]{2,40}?)\s{2,}\S/.exec(s);
    return m ? m[1].trim() : "";
  }

  function lines(v) {
    if (Array.isArray(v)) return v.filter(Boolean);
    return String(v == null ? "" : v).split(/\n+/).map(function (x) { return x.trim(); }).filter(Boolean);
  }

  function step(s) {
    return {
      role: s.role || "",
      name: s.product_title || s.product_handle || "",
      handle: s.product_handle || "",
      size: s.size_label || "",
      desc: s.body || "",
      alt: s.alt_handle || null,
      altName: s.alt_title || s.alt_handle || "",
      altConjunction: s.alt_conjunction || "alternating with",
      price: "",
    };
  }

  function fromRecord(record, P) {
    record = record || {};
    P = P || {};
    var prof = record.profile || {};
    var reg = P.regimen || {};
    var pack = P.pack || {};

    /* The engine writes these as the STRING "1" on the curated hand-off
       object — not as booleans, and not at the top level of the record. */
    var flag = function (k) { return String(prof[k] || "") === "1"; };
    var pregnant = ["yes", "true", "pregnant", "1"]
      .indexOf(String(prof.pregnant == null ? "" : prof.pregnant).trim().toLowerCase()) > -1;

    var comps = deriveComponents(P.title || record.result_word || "");
    var flags = deriveFlags(comps.comps, comps.teen, comps.modifier);
    flags.teen = comps.teen || (P.cohort === "teen" || P.cohort === "tween");
    if (flags.teen) flags.aging = false;

    return {
      customer: {
        firstName: prof.first_name || "",
        lastName: prof.last_name || "",
        email: prof.email || "",
      },
      date: (prof.submit_date || record.submitted_at || "").slice(0, 10) || todayISO(),
      code: P.code || record.result_code || "",
      title: P.title || "",
      flags: flags,
      scores: {
        aging: num(prof.aging_perc), hydration: num(prof.hyd_perc),
        pigmentation: num(prof.pigm_perc), acne: num(prof.acne_perc),
        allergy: num(prof.allergy_perc), redness: num(prof.ros_perc),
        sensitivity: num(prof.sens_perc),
      },
      pregnant: pregnant,
      /* Dedicated pregnancy protocol vs the base one adapted. /v1/diagnosis
         reports the served protocol's own nature; the engine's routing flag is
         the second witness, because an older record may carry only one. */
      pregnancyOf: (P.pregnancy && P.pregnancy.is_pregnancy)
        ? (P.pregnancy.parent_code || P.code) : (flag("pregnancy_protocol") ? P.code : null),
      profileMissing: flag("profile_missing"),
      pregnancyTeen: flag("pregnancy_teen"),
      steroids: String(prof.steroid_use == null ? "" : prof.steroid_use),
      pigmentRiskOnly: flag("pigm_risk_only"),
      previous: prof.previous_dd
        ? { code: String(prof.previous_dd).toUpperCase().trim(), date: prof.submit_date_previous || "" }
        : null,
      profile: {
        title: P.title || "",
        characteristics: lines(P.characteristics),
        description: lines(P.description),
        frustrations: lines(P.frustrations),
        goals: lines(P.goals),
      },
      objectives: (P.objectives || []).map(function (o) {
        return { t: o.title || "", d: o.body || "" };
      }),
      ingredients: (P.actives || []).map(function (a) {
        return {
          name: a.name || a.slug || "",
          role: leadHeading(a.benefits) || "",
          tag: a.inci_name || "",
        };
      }),
      cautions: (reg.cautions || []).map(function (c) {
        return { title: c.title || "", subtext: c.subtext || "", items: lines(c.body || c.items) };
      }),
      regimen: {
        morning: (reg.am || []).map(step),
        evening: (reg.pm || []).map(step),
        notes: (reg.general || []).reduce(function (all, n) { return all.concat(lines(n.body || n.items)); }, []),
      },
      pack: {
        handle: pack.handle || "",
        lede: pack.lede || "",
        first: pack.first || "",
        items: (pack.items || []).map(function (i) {
          return {
            handle: i.handle, name: i.title || i.handle, size: i.size_label || "",
            price: i.reference_price_zar == null ? null : i.reference_price_zar,
            variantGroup: i.variant_group || null, variantValue: i.variant_value || null,
            optional: !!i.optional,
          };
        }),
        gifts: (pack.gifts || []).map(function (g) {
          return {
            handle: g.handle, name: g.title || g.handle, size: g.size_label || "",
            variantGroup: g.variant_group || null, variantValue: g.variant_value || null,
          };
        }),
      },
    };
  }

  window.DD_PRINT = { build: build, mount: mount, fromRecord: fromRecord,
                      copy: copy, notice: notice };
})();
